/**
 * ADR-0235 — ops2026 control for merchant-visible trust verdicts.
 *
 *   - Global kill switch (`trust_verdicts_kill_switch`): on = no merchant
 *     sees verdicts anywhere, whatever the per-site flags say.
 *   - Per-site flag `trust_verdicts_live` (default off): list of sites
 *     with a flag row; a live site can be turned off here. Turning a site
 *     ON happens in the Readiness section (ADR-0236 go-live gate).
 *
 * Every change is staff-audited server-side. A 404 (API not deployed yet)
 * hides the panel body behind a one-line note.
 */

import { useCallback, useEffect, useState } from 'react';

import type { AdminTrustVerdictFlagsResponse } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export function LiveVerdictsPanel({ reloadToken = 0, onChanged }: { reloadToken?: number; onChanged?: () => void }): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [data, setData] = useState<AdminTrustVerdictFlagsResponse | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await api.getTrustVerdictFlags());
      setMissing(false);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'not_found') setMissing(true);
      else setError(err instanceof ApiError ? err.message : 'Failed to load trust-verdict flags.');
    }
  }, [api]);

  useEffect(() => { void load(); }, [load, reloadToken]);

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The change did not save.');
    } finally {
      setBusy(false);
    }
  };

  const btn = (primary: boolean): React.CSSProperties => ({
    fontFamily: sansFont, fontSize: 12, padding: '5px 10px', borderRadius: 3, cursor: busy ? 'default' : 'pointer',
    background: primary ? C.text : 'transparent', color: primary ? C.bg : C.body, border: `1px solid ${primary ? C.text : C.border}`,
    opacity: busy ? 0.6 : 1,
  });

  return (
    <Card style={{ padding: 16 }} data-testid="live-verdicts-panel">
      <div style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text }}>Merchant-visible verdicts</div>
      <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
        Off by default. A site shows trust labels, the Why? list and the dispute button only when its flag is on and the global kill switch is off. Turn a site on from Readiness below — only sites that meet the targets, or with a recorded override.
      </div>
      {missing ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 10 }}>Not available until the API release that adds it is live.</div>
      ) : null}
      {error !== null ? <div role="alert" style={{ fontFamily: sansFont, fontSize: 12.5, color: C.coralFg, marginTop: 10 }}>{error}</div> : null}
      {data !== null ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: C.muted }}>Global kill switch</span>
            <Pill tone={data.kill_switch ? 'coral' : 'neutral'}>{data.kill_switch ? 'ON — nothing shown' : 'off'}</Pill>
            <button
              type="button"
              disabled={busy}
              data-testid="kill-switch-toggle"
              onClick={() => void run(() => api.setSetting('trust_verdicts_kill_switch', !data.kill_switch))}
              style={btn(false)}
            >
              {data.kill_switch ? 'Turn kill switch off' : 'Turn kill switch on'}
            </button>
          </div>

          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: C.muted, marginTop: 16, marginBottom: 6 }}>Sites</div>
          {data.sites.length === 0 ? (
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>No site has verdicts turned on.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.sites.map((s) => (
                <div key={s.site_id} data-testid={`flag-${s.site_id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: `1px solid ${C.border}`, paddingBottom: 6 }}>
                  <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, minWidth: 160 }}>{s.site_domain ?? '(deleted site)'}</span>
                  <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{s.site_id}</span>
                  <Pill tone={s.verdicts_live ? 'lime' : 'neutral'}>{s.verdicts_live ? 'live' : 'off'}</Pill>
                  <span style={{ fontFamily: monoFont, fontSize: 10.5, color: C.dim }}>{formatIsoCompact(s.updated_at)}</span>
                  {s.verdicts_live ? (
                    <button type="button" disabled={busy} onClick={() => void run(() => api.setTrustVerdictFlag(s.site_id, false))} style={btn(false)} data-testid={`flag-off-${s.site_id}`}>
                      Turn off
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
    </Card>
  );
}
